import { CommonModule, NgSwitch } from '@angular/common';
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FeedbackService } from '@services/services/feedback.service';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, ElementSize, InputComponent, MbsSize, SizeObserverDirective } from 'mbs-ui-kit';
import { RoutingPath } from 'mbs-ui-shared@constants/app-routing-path.enum';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { take } from 'rxjs';
import { filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-feedback-widget',
  templateUrl: './feedback-widget.component.html',
  styleUrls: ['./feedback-widget.component.scss'],
  imports: [CommonModule, ButtonComponent, NgSwitch, ReactiveFormsModule, I18NextPipe, InputComponent, SizeObserverDirective]
})
export class FeedbackWidgetComponent implements OnInit {
  private service = inject(FeedbackService);
  private router = inject(Router);
  private auth = inject(AuthFacade);

  public readonly mbsSize = MbsSize;
  public readonly starSize = 35;
  public readonly textAreaMinSize = 135;
  public readonly textAreaGapSize = 7;
  public readonly halfStarSize = this.starSize / 2;
  public readonly starGap = 16;
  public readonly elementSelectors = {
    css: {
      root: 'feedback-widget',
      title: 'feedback-widget_title',
      padding: 'feedback-widget_padding',
      body: 'feedback-widget_body',
      content: 'feedback-widget_body-content',
      stars: 'feedback-widget_body-stars',
      icon: 'feedback-widget_body-star-icon',
      textarea: 'feedback-widget_body-textarea'
    }
  };

  public hoveredValue = 0;
  public ratingSelected = false;
  public loading = false;
  public interacted = false;
  public textareaSize = signal<ElementSize>({} as ElementSize);
  public bottomMargin = computed(() => {
    if (this.opened()) return 0;

    const height = this.textareaSize()?.height ?? 0;
    const value = this.textAreaMinSize - height + this.textAreaGapSize;

    return height > 0 && value < 0 ? value : 0;
  });
  public visible = computed(() => this._forced() || this._visible());
  public opened = signal<boolean>(false);
  public form: FormGroup<{ rating: FormControl<number>; description: FormControl<string> }> = new FormGroup<{
    rating: FormControl<number>;
    description: FormControl<string>;
  }>({
    rating: new FormControl<number>(0),
    description: new FormControl<string>('')
  });

  private _visible = signal<boolean>(false);
  private _forced = signal<boolean>(false);
  private knownPaths = Object.keys(RoutingPath).map((key) => RoutingPath[key]);

  constructor() {
    effect(() => {
      this.interacted = this.interacted || this.opened();
    });
    effect(() => this.service.isWidgetShown.next(this.visible()));
  }

  ngOnInit() {
    let lastPathName = this.getActualPath();

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe(() => {
        const newPathName = this.getActualPath();

        if (lastPathName !== newPathName) {
          lastPathName = newPathName;

          this.updateVisibility();
        }
      });

    this.service.forceOpenWidget.pipe(untilDestroyed(this)).subscribe((value) => {
      if (value.force) {
        this.open(value.force, value.collapsed);
      }
    });

    this.updateVisibility();
  }

  open(force?: boolean, collapsed?: boolean): void {
    if (force && !this._visible()) {
      this._forced.set(true);
    }

    this.opened.set(!collapsed);
    this._visible.set(true);
  }

  toggle(): void {
    this.opened.set(!this.opened());
  }

  setHovered(rating: number) {
    this.hoveredValue = rating;
  }

  rate(rating: number) {
    this.ratingSelected = true;
    this.form.controls.rating.setValue(rating);
  }

  clearHovered() {
    this.hoveredValue = 0;
  }

  getStarFill(starIndex: number): 'full' | 'half' | 'empty' {
    const displayValue = this.hoveredValue || this.form.controls.rating.value;
    if (displayValue >= starIndex) return 'full';
    if (displayValue >= starIndex - 0.5) return 'half';

    return 'empty';
  }

  remindMeLater(period: 3 | 30): void {
    const final = () => {
      this._visible.set(false);
      this._forced.set(false);
      this.loading = false;
    };

    if (this.loading) {
      return;
    }

    if (this._forced()) {
      return void final();
    }

    this.loading = true;
    this.service.remindMeLater(period).subscribe(final);
  }

  send(): void {
    if (this.loading) {
      return;
    }

    const form = this.form.value;
    const additionalInfo = `{ page: '${window.location.href}' }`;
    const description = form.description ? `${form.description}\r\n\r\n${additionalInfo}` : additionalInfo;

    this.loading = true;
    this.service.send(form.rating * 2, description).subscribe(() => {
      this._visible.set(false);
      this._forced.set(false);

      this.loading = false;
      this.ratingSelected = false;
      this.hoveredValue = 0;

      this.form.reset({ rating: 0, description: '' });
    });
  }

  private updateVisibility(): void {
    if (!this.service.canShowWidgetByCurrentUrl()) {
      this._visible.set(false);
      this._forced.set(false);

      return;
    }

    this.service
      .canShowWidget()
      .pipe(untilDestroyed(this))
      .subscribe((visible) => this._visible.set(visible));
  }

  private getActualPath(): string {
    return this.knownPaths.find((path) => window.location.pathname.startsWith(path));
  }

  handleEarnMoneyClick(): void {
    this.auth.currentUser$
      .pipe(take(1), untilDestroyed(this))
      .subscribe((user) => window.open(`https://www.msp360.com/offers/review/msp360-platform/?email=${user.Email}`, '_blank'));
  }
}
